// The General shows, then does (decision 0009): "Show me" takes the player to the very button a card would press
// and makes it blink; "Do it" goes there first, acts, then leaves the path on screen so the player can do it alone
// next time. This module holds the signals the screens read; Game.tsx drives them.
import { signal } from '@preact/signals';
import type { Command, Resource } from '@aurane/protocol';

/** How long a « Here: … Tap the blinking button » hint may stay at most (issues #39, #55): long enough to read and reach
 *  the button, short enough not to linger over the next screen. */
export const TEACH_MS = 8000;

/** The control being pointed at (each button knows its key), the sentence that says where it lives, and the screen
 *  it was said on (`screen`, from the provider Game.tsx registers) with the time it was said. */
export interface Teaching { key: string; hint: string; done: boolean; screen: string; at: number; ms: number }
export const teach = signal<Teaching | null>(null);
let timer: ReturnType<typeof setTimeout> | null = null;
let screenNow = (): string => '';

/** Game.tsx says what « the screen » is (tab, open system, link mode), so a hint knows when the player went elsewhere. */
export function watchScreen(fn: () => string): void { screenNow = fn; }

/**
 * When a hint ends (issue #39), besides its ✕ and a tap on the blinking button:
 * - `time`: after its lifetime (`TEACH_MS` by default);
 * - `command`: a pointing hint ends when the command it demonstrates is sent (any command when it points at no
 *   button); the « Done ✓ next time » line ends on the player's next command;
 * - `screen`: a pointing hint ends when the player chooses another screen or cancels the mode it opened (the link
 *   mode); the « Done ✓ » line stays through the camera and panel moves that follow the General's action.
 */
export type TeachEvent = { kind: 'time'; now: number } | { kind: 'command'; key: string | null } | { kind: 'screen'; screen: string };
export function teachEnds(tv: Pick<Teaching, 'key' | 'done' | 'screen' | 'at' | 'ms'> | null, ev: TeachEvent): boolean {
  if (!tv) return false;
  switch (ev.kind) {
    case 'time': return ev.now - tv.at >= tv.ms;
    case 'command': return tv.done || !tv.key || ev.key === tv.key;
    case 'screen': return !tv.done && ev.screen !== tv.screen;
  }
}

export function pointAt(key: string | null, hint: string, done = false, ms = TEACH_MS): void {
  const life = Math.min(ms, TEACH_MS);
  teach.value = { key: key ?? '', hint, done, screen: screenNow(), at: Date.now(), ms: life };
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { teach.value = null; timer = null; }, life);
}

export function stopTeaching(): void { if (timer) clearTimeout(timer); timer = null; teach.value = null; }

/** Ends the current hint if this event ends it. */
export function teachEvent(ev: TeachEvent): void { if (teachEnds(teach.value, ev)) stopTeaching(); }

/** Class for a control: 'teach' while the General points at it. Components read `teach` once and call this. */
export const teachClass = (tv: { key: string; done: boolean } | null, key: string): string => (tv && !tv.done && tv.key === key ? 'teach' : '');

/** Where the system view lands when it opens for a demonstration: which dock tab, which orbit's free slot. */
export const pendingDemo = signal<{ system: string; dock: 'plateau' | 'fleets'; orbit: 1 | 2 | 3 | null } | null>(null);

/** Arms the system scene to flash the next thing that appears on the plateau (bumped right before the General acts). */
export const sceneFlashReq = signal(0);

/** The Market form pre-filled with the General's order, so "Show me" shows the exact order to place. */
export const marketPrefill = signal<{ region: string; resource: Resource; side: 'buy' | 'sell'; qty: number; price: number } | null>(null);

/** The key of the button that would send this command, when a screen has one. */
export function teachKey(cmd: Command): string | null {
  switch (cmd.type) {
    case 'build_relay': return `link:${cmd.b}`;
    case 'build': return `build:${cmd.building}`;
    case 'train': return `train:${cmd.unit}`;
    case 'fleet_order': return `fleet:${cmd.order}:${cmd.fleet}`;
    case 'market_order': return 'market:place';
    case 'treaty': return `treaty:${cmd.kind}:${cmd.with}`;
    case 'decree': return `decree:${cmd.kind}`;
    default: return null;
  }
}
